<template>
  <div class="bui-bulk-actions">
    <button
      v-if="primary"
      type="button"
      class="cta cta-pri"
      @click="onPrimaryClick"
    >
      <span v-if="primary.icon" class="ico">
        <component :is="primary.icon" :size="20" weight="bold" />
      </span>
      <span class="label">{{ primary.label }}</span>
    </button>

    <div v-if="secondaryActions.length" ref="secondaryRootRef" class="cta-sec-wrap">
      <button
        type="button"
        class="cta cta-sec"
        :class="{ disabled: secondaryDisabled, open: isOpen }"
        :disabled="secondaryDisabled"
        :aria-expanded="isOpen"
        :aria-haspopup="true"
        @click="toggle"
      >
        <span class="label">{{ secondaryLabel }}</span>
        <PhCaretDown :size="16" weight="bold" />
      </button>

      <Teleport to="body">
        <!--
          @click.stop: teleported outside the lister root, so without it a
          menu click reaches document.body and clears the list selection
          (useListSelection's clearSelectionTriggerElement) mid-action.
        -->
        <div
          v-if="isOpen && menuRect"
          class="bui-bulk-actions-menu"
          :style="menuPosStyle"
          role="menu"
          @click.stop
        >
          <button
            v-for="act in secondaryActions"
            :key="act.value"
            type="button"
            class="bui-bulk-actions-menu__item"
            :class="{ disabled: act.disabled }"
            :disabled="act.disabled"
            role="menuitem"
            @click="onActionClick(act)"
          >
            {{ act.label }}
          </button>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { PhCaretDown } from '@phosphor-icons/vue';

export interface PrimaryAction {
  label: string;
  icon?: any;
}

export interface SecondaryAction {
  label: string;
  value: string;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    primary?: PrimaryAction | null;
    secondaryActions?: SecondaryAction[];
    secondaryLabel?: string;
    secondaryDisabled?: boolean;
  }>(),
  {
    primary: null,
    secondaryActions: () => [],
    secondaryLabel: 'Actions',
    secondaryDisabled: false,
  },
);

const emit = defineEmits<{
  primary: [];
  action: [value: string];
}>();

const secondaryRootRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const menuRect = ref<DOMRect | null>(null);

function onPrimaryClick() {
  emit('primary');
}

function toggle() {
  if (props.secondaryDisabled) return;
  if (isOpen.value) {
    isOpen.value = false;
  } else {
    measure();
    isOpen.value = true;
  }
}

function measure() {
  const trigger = secondaryRootRef.value?.querySelector('.cta-sec') as HTMLElement | null;
  menuRect.value = trigger?.getBoundingClientRect() || null;
}

function onActionClick(action: SecondaryAction) {
  if (action.disabled) return;
  isOpen.value = false;
  emit('action', action.value);
}

const menuPosStyle = computed(() => {
  if (!menuRect.value) return {};
  return {
    position: 'fixed' as const,
    top: `${menuRect.value.bottom + 8}px`,
    left: `${menuRect.value.left}px`,
    minWidth: `${menuRect.value.width}px`,
  };
});

function onDocClick(e: MouseEvent) {
  if (!isOpen.value) return;
  const t = e.target as Node | null;
  if (!t) return;
  if (secondaryRootRef.value?.contains(t)) return;
  const menus = document.querySelectorAll('.bui-bulk-actions-menu');
  for (const m of menus) {
    if (m.contains(t)) return;
  }
  isOpen.value = false;
}

function onWindowChange() {
  if (isOpen.value) measure();
}

onMounted(() => {
  document.addEventListener('click', onDocClick, true);
  window.addEventListener('resize', onWindowChange);
  window.addEventListener('scroll', onWindowChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true);
  window.removeEventListener('resize', onWindowChange);
  window.removeEventListener('scroll', onWindowChange, true);
});

watch(isOpen, (v) => {
  if (v) measure();
});
</script>

<style lang="scss" scoped>
.bui-bulk-actions {
  display: inline-flex;
  align-items: center;
  gap: 16px;
}

.cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: var(--bui-cta-h, 52px);
  padding: 0 24px;
  border: 0;
  border-radius: var(--bui-cta-radius, 8px);
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  line-height: 17px;
  font-weight: 500;
  transition: background-color 120ms ease;
  white-space: nowrap;
}

.cta-pri {
  background: var(--bui-cta-pri-fill, #baff5c);
  color: var(--bui-cta-pri-text, #007366);

  .ico {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }

  &:hover {
    filter: brightness(0.96);
  }
}

.cta-sec {
  background: var(--bui-cta-sec-fill, #4b1886);
  color: var(--bui-cta-sec-text, #fffcfd);

  &:hover:not(.disabled) {
    filter: brightness(1.08);
  }

  &.disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.cta-sec-wrap {
  position: relative;
  display: inline-flex;
}

@media (max-width: 768px) {
  .bui-bulk-actions {
    gap: 8px;
    flex-wrap: wrap;
  }

  .cta {
    height: var(--bui-cta-h, 34px);
    min-height: 34px;
    padding: 0 12px;
    font-size: 12px;
    line-height: 15px;
    gap: 6px;
  }

  .cta-pri .ico :deep(svg) {
    width: 16px;
    height: 16px;
  }
}
</style>

<style lang="scss">
.bui-bulk-actions-menu {
  background: var(--bui-droplet-menu-fill, #fffcfd);
  color: var(--bui-droplet-menu-text, #2a2969);
  border-radius: var(--bui-droplet-menu-radius, 8px);
  box-shadow: 0 8px 32px rgba(75, 24, 134, 0.32);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 9999;
  min-width: 160px;

  &__item {
    display: flex;
    align-items: center;
    height: 36px;
    padding: 0 12px;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--bui-droplet-menu-text, #2a2969);
    font-family: inherit;
    font-size: 14px;
    line-height: 17px;
    cursor: pointer;
    text-align: left;

    &:hover {
      background: rgba(42, 41, 105, 0.06);
    }

    &.disabled {
      opacity: 0.45;
      cursor: default;

      &:hover {
        background: transparent;
      }
    }
  }
}
</style>
